/** Рендер блоков канонического формата.
 *
 * Сервер присылает чистый текст и отдельный список диапазонов разметки, а не
 * HTML. Здесь диапазоны превращаются в вложенные элементы, поэтому в разметку
 * страницы физически не может попасть чужой тег из файла книги.
 */

import type { ReactNode } from 'react'
import type { Block } from '../api'
import { imageUrl } from '../api'

const STYLE_TAG: Record<string, keyof React.JSX.IntrinsicElements> = {
  em: 'em',
  strong: 'strong',
  sub: 'sub',
  sup: 'sup',
  code: 'code',
  strike: 's',
  note: 'sup',
}

/** Разрезает текст на отрезки с одинаковым набором стилей. */
export function renderSpans(text: string, spans?: [number, number, string][]): ReactNode {
  if (!spans || spans.length === 0) return text

  const bounds = new Set<number>([0, text.length])
  for (const [start, end] of spans) {
    if (start >= 0 && start <= text.length) bounds.add(start)
    if (end >= 0 && end <= text.length) bounds.add(end)
  }
  const points = [...bounds].sort((a, b) => a - b)

  const parts: ReactNode[] = []
  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i]
    const to = points[i + 1]
    if (to <= from) continue
    const chunk = text.slice(from, to)
    const active = spans
      .filter(([start, end]) => start <= from && end >= to)
      .map(([, , kind]) => kind)

    let node: ReactNode = chunk
    // Вкладываем от внутреннего к внешнему, чтобы порядок был предсказуем.
    for (const kind of active) {
      const Tag = STYLE_TAG[kind]
      if (Tag) node = <Tag>{node}</Tag>
    }
    parts.push(<span key={from}>{node}</span>)
  }
  return parts
}

interface BlockProps {
  block: Block
  bookId: number
}

export function BlockView({ block, bookId }: BlockProps) {
  const content = block.c ? renderSpans(block.c, block.s) : null

  switch (block.t) {
    case 'h': {
      const level = Math.min(Math.max(block.lvl ?? 1, 1), 3)
      const Tag = (`h${level}` as 'h1' | 'h2' | 'h3')
      return <Tag>{content}</Tag>
    }
    case 'subtitle':
      return <p className="subtitle">{content}</p>
    case 'cite':
      return <p className="cite">{content}</p>
    case 'epigraph':
      return <p className="epigraph">{content}</p>
    case 'v':
      return <p className="verse">{content}</p>
    case 'img':
      return block.src ? <img src={imageUrl(bookId, block.src)} alt="" loading="lazy" /> : null
    case 'hr':
      return <hr />
    case 'empty':
      return <div className="empty" />
    default:
      return <p>{content}</p>
  }
}
